import { Define, SetAttr } from '../../../core/decorators';
import { TAG_INPUT_DATE } from '../../../core/utils/constants/tags';
import { HTMLBnumInputNumber } from '../input-number/bnum-input-number';
import { HTMLBnumInput } from '../input/bnum-input';
import { ATTRIBUTE_TYPE } from '../input/bnum-input.internal/constants';
import { BnumInputNumericCreateOptions } from '../input/bnum-input.internal/types';

const TYPE = 'date';

/**
 * Input de date.
 *
 * @category Input
 *
 * @structure Sans rien
 * <bnum-input-date></bnum-input-date>
 *
 * @structure Avec une légende
 * <bnum-input-date>Label du champ</bnum-input-date>
 *
 * @structure Avec une légende et un indice
 * <bnum-input-date>
 * Label du champ
 * <span slot="hint">Indice d'utilisation</span>
 * </bnum-input-date>
 *
 * @structure Avec un bouton
 * <bnum-input-date button="true" button-icon="add">Label du champ
 *   <span slot="button">Ajouter</span>
 * </bnum-input-date>
 *
 * @structure En erreur
 * <bnum-input-date min="2025-01-01" data-value="2024-01-01">Label du champ
 * </bnum-input-date>
 *
 * @structure Avec un état de succès
 * <bnum-input-date state="success">Label du champ
 *   <span slot="success">Le champ est valide !</span>
 * </bnum-input-date>
 *
 * @structure Avec une icône
 * <bnum-input-date icon="search">Label du champ</bnum-input-date>
 *
 * @structure Avec un bouton avec icône seulement
 * <bnum-input-date placeholder="LA LA !" button-icon="add">Label du champ
 * </bnum-input-date>
 *
 * @structure Désactivé
 * <bnum-input-date disabled>
 *   Label du champ
 * </bnum-input-date>
 *
 * @structure Complet
 * <bnum-input-date
 *   data-value="5"
 *   placeholder="Texte indicatif"
 *   type="text"
 *   state="error"
 *   icon="search"
 *   button="primary"
 *   button-icon="send"
 *   step="10"
 * >
 *   Label du champ
 *   <span slot="hint">Indice d'utilisation</span>
 *   <span slot="success">Le champ est valide !</span>
 *   <span slot="error">Le champ est invalide !</span>
 *   <span slot="button">Envoyer</span>
 * </bnum-input-date>
 *
 * @attr {string} (optional) (default: 'number') type - Type de l'input (text, password, email, etc.) Ne pas modifier, toujours 'number' pour ce composant.
 *
 */
@Define({ tag: TAG_INPUT_DATE })
export class HTMLBnumInputDate extends HTMLBnumInput {
	constructor() {
		super();
	}

	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [
			...super._p_getStylesheets(),
			HTMLBnumInputNumber.AdditionnalStylesheet,
		];
	}

	@SetAttr(ATTRIBUTE_TYPE, TYPE)
	protected _p_preload(): void {}

	/**
	 *@inheritdoc
	 */
	protected _p_buildDOM(): void {
		super._p_buildDOM();
	}

	/**
	 *@inheritdoc
	 */
	static _p_observedAttributes(): string[] {
		return super._p_observedAttributes().filter(x => x !== ATTRIBUTE_TYPE);
	}

	/**
	 * Crée une instance du composant avec les options fournies.
	 * @param label Texte du label principal.
	 * @param options Options d'initialisation (attributs et slots).
	 * @returns Instance du composant.
	 */
	static Create(
		label: string,
		options: BnumInputNumericCreateOptions = {},
	): HTMLBnumInputDate {
		const finalOptions = {
			type: TYPE,
			...options,
		};
		return super.Create(label, finalOptions) as HTMLBnumInputDate;
	}
}
